import React, { useState, useRef } from 'react';
import { useChat } from '../context/ChatContext';
import { fileService } from '../services/apiService';
import { Send, Paperclip, X, Image as ImageIcon, Mic, Square, Camera } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const MessageInput = () => {
  const { selectedOrder, sendMessage, setTyping } = useChat();
  const { toast } = useToast();
  const [message, setMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingIntervalRef = useRef(null);

  const handleTyping = (value) => {
    setMessage(value);

    if (value && !isTyping) {
      setIsTyping(true);
      setTyping(selectedOrder?.id, true);
    } else if (!value && isTyping) {
      setIsTyping(false);
      setTyping(selectedOrder?.id, false);
    }
  };

  const handleFileSelect = (e, type = 'file') => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (type === 'image' && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleSend = async () => {
    if (!selectedOrder) return;

    try {
      if (selectedFile) {
        setUploading(true);

        // Upload file to backend
        const uploadResult = await fileService.uploadFile(selectedFile);

        // Send message with file URL
        await sendMessage(selectedOrder.id, uploadResult.url, uploadResult.type);

        toast({
          title: 'File sent',
          description: `${selectedFile.name} has been sent`,
        });

        setSelectedFile(null);
        setPreviewUrl(null);
        setUploading(false);
      } else if (message.trim()) {
        await sendMessage(selectedOrder.id, message.trim(), 'text');
        setMessage('');
        setIsTyping(false);
        setTyping(selectedOrder?.id, false);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send message',
        variant: 'destructive',
      });
      setUploading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (imageInputRef.current) imageInputRef.current.value = '';
  };


  const startRecording = async () => {
    console.log('🎤 Starting recording...');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('✅ Microphone access granted');

      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        console.log('⏹️ Recording stopped, uploading...');
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioFile = new File([audioBlob], `voice-${Date.now()}.webm`, { type: 'audio/webm' });

        // Upload and send
        try {
          setUploading(true);
          const result = await fileService.uploadFile(audioFile);
          await sendMessage(selectedOrder.id, result.url, 'voice');
          console.log('✅ Voice note sent successfully');
          alert('Voice note sent!');
        } catch (error) {
          console.error('❌ Failed to send voice note:', error);
          alert('Failed to send voice note: ' + error.message);
        } finally {
          setUploading(false);
        }

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      console.log('🔴 Recording started');
      alert('Recording started! Click stop button to send.');

    } catch (error) {
      console.error('❌ Microphone error:', error);
      alert('Microphone access denied. Please allow microphone permission and try again.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingIntervalRef.current);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      setRecordingTime(0);
      clearInterval(recordingIntervalRef.current);
      audioChunksRef.current = [];
      toast({ title: 'Recording cancelled' });
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!selectedOrder) {
    return null;
  }

  return (
    <div className="bg-[#f0f2f5] px-4 py-3 border-t border-gray-200">
      {/* File Preview */}
      {selectedFile && (
        <div className="mb-3 p-3 bg-white rounded-lg shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {previewUrl ? (
                <img src={previewUrl} alt="Preview" className="w-16 h-16 object-cover rounded" />
              ) : (
                <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                  <Paperclip className="w-6 h-6 text-gray-500" />
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-gray-800">{selectedFile.name}</p>
                <p className="text-xs text-gray-500">
                  {(selectedFile.size / 1024).toFixed(2)} KB
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFile}
              className="text-gray-500 hover:text-red-500"
              disabled={uploading}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      )}

      {/* Input Area */}
      {isRecording ? (
        /* Recording UI */
        <div className="flex items-center space-x-3 bg-white rounded-full px-4 py-3 shadow-sm">
          <div className="flex items-center space-x-2 flex-1">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            <span className="text-red-500 font-medium">{formatTime(recordingTime)}</span>
            <div className="flex-1 flex items-center space-x-1">
              {[...Array(20)].map((_, i) => (
                <div key={i} className="w-1 bg-emerald-400 rounded-full animate-pulse" style={{
                  height: `${Math.random() * 20 + 10}px`,
                  animationDelay: `${i * 50}ms`
                }}></div>
              ))}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={cancelRecording}
            className="text-red-500 hover:bg-red-50"
          >
            <X className="w-5 h-5" />
          </Button>
          <Button
            onClick={stopRecording}
            className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
      ) : (
        /* Normal Input UI */
        <div className="flex items-end space-x-2">
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-600 hover:text-emerald-600"
              onClick={() => cameraInputRef.current?.click()}
              disabled={uploading}
              title="Take photo"
            >
              <Camera className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-600 hover:text-emerald-600"
              onClick={() => imageInputRef.current?.click()}
              disabled={uploading}
              title="Choose from gallery"
            >
              <ImageIcon className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-600 hover:text-emerald-600"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <Paperclip className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex-1 bg-white rounded-full px-4 py-2 shadow-sm">
            <textarea
              value={message}
              onChange={(e) => handleTyping(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message"
              className="w-full resize-none outline-none text-gray-800 placeholder-gray-400"
              rows="1"
              style={{ maxHeight: '120px', minHeight: '24px' }}
              disabled={uploading}
            />
          </div>

          {message.trim() || selectedFile ? (
            <Button
              onClick={handleSend}
              disabled={uploading}
              className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full w-12 h-12 p-0 flex-shrink-0"
            >
              {uploading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          ) : (
            <Button
              onClick={isRecording ? stopRecording : startRecording}
              className={`${isRecording ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-500 hover:bg-emerald-600'} text-white rounded-full w-12 h-12 p-0 flex-shrink-0`}
            >
              {isRecording ? <Square className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </Button>
          )}
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFileSelect(e, 'image')}
      />
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFileSelect(e, 'image')}
      />
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={(e) => handleFileSelect(e, 'file')}
      />
    </div>
  );
};

export default MessageInput;
